import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSnackbar } from 'notistack';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useAppActions, useAppState } from '@afosto/app-components-react/AppProvider';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import Switch from '@afosto/app-components-react/Switch';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import FulfilmentLocationSearchForm from '../../../../components/FulfilmentLocationSearchForm';
import useFulfilmentLocations from '../../../../utils/useFulfilmentLocations';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import { locationsValidationSchema } from './validationSchema';
import translations from './translations';

const ShipmentsSettings = () => {
  const intl = useIntl();
  const { appKey, settings } = useAppState();
  const { enqueueSnackbar } = useSnackbar();
  const [actions] = useAppActions();
  const [showLocationsDrawer, setShowLocationsDrawer] = useState(false);
  const { shipments: shipmentsSettings } = settings ?? {};
  const {
    show_create_shipment_button_mobile: showCreateShipmentButtonMobile = true,
    always_unpack_inbound_shipments: alwaysUnpackInboundShipments = true,
    dont_print_packing_slip_for_destination_locations: dontPrintPackingSlipForDestinationLocations = [],
  } = shipmentsSettings ?? {};

  const locations = useFulfilmentLocations();
  const dontPrintPackingSlipLocations = dontPrintPackingSlipForDestinationLocations.reduce(
    (acc, locationId) => {
      const location = findObjectInArray(locations, 'id', locationId);

      if (location) {
        return [...acc, { ...location, label: location.name, value: location.id }];
      }

      return acc;
    },
    [],
  );

  const handleUpdateSetting = key => async (ev, newValue) => {
    try {
      if (newValue === shipmentsSettings?.[key]) {
        return;
      }

      const response = await profileApi.updateProfile(
        `afosto.tenant.${appKey}.shipments.v1.settings.shipments`,
        {
          ...shipmentsSettings,
          [key]: newValue,
        },
      );
      const { data } = response;

      actions.setSettings({ ...settings, shipments: data });

      enqueueSnackbar(intl.formatMessage(translations.feedback.settingsUpdated), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.settingsNotUpdated), {
        variant: 'error',
      });
    }
  };

  const handleSetLocationSettings = async ({ locations: selectedLocations }) => {
    const locationIds = selectedLocations.map(location => location.id);
    await handleUpdateSetting('dont_print_packing_slip_for_destination_locations')({}, locationIds);
    setShowLocationsDrawer(false);
  };

  return (
    <>
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <Divider />
        <List dense>
          <ListItem divider>
            <ListItemText
              primary={intl.formatMessage(
                translations.settings.dontPrintPackingSlipForDestinationLocations,
              )}
              secondary={
                dontPrintPackingSlipLocations.length > 0
                  ? dontPrintPackingSlipLocations.map(location => location.name).join(', ')
                  : intl.formatMessage(translations.feedback.noLocationsSelected)
              }
            />
            <ListItemSecondaryAction>
              <Button onClick={() => setShowLocationsDrawer(true)} variant="outlined">
                <FormattedMessage {...translations.actions.edit} />
              </Button>
            </ListItemSecondaryAction>
          </ListItem>
          <ListItem divider>
            <ListItemText
              primary={intl.formatMessage(translations.settings.alwaysUnpackInboundShipments)}
            />
            <Box ml={1}>
              <Switch
                name="alwaysUnpackInboundShipments"
                color="primary"
                checked={alwaysUnpackInboundShipments}
                onChange={handleUpdateSetting('always_unpack_inbound_shipments')}
                changeOnMounted={false}
              />
            </Box>
          </ListItem>
          <ListItem>
            <ListItemText
              primary={intl.formatMessage(translations.settings.showCreateShipmentButtonMobile)}
            />
            <Box ml={1}>
              <Switch
                name="showCreateShipmentButtonMobile"
                color="primary"
                checked={showCreateShipmentButtonMobile}
                onChange={handleUpdateSetting('show_create_shipment_button_mobile')}
                changeOnMounted={false}
              />
            </Box>
          </ListItem>
        </List>
      </Card>
      <FormDrawer
        isOpen={showLocationsDrawer}
        title={intl.formatMessage(translations.locationsDrawer.title)}
        drawerWidth="420px"
        onClose={() => setShowLocationsDrawer(false)}
      >
        <FulfilmentLocationSearchForm
          onCancel={() => setShowLocationsDrawer(false)}
          onSubmit={handleSetLocationSettings}
          locations={dontPrintPackingSlipLocations}
          validationSchema={locationsValidationSchema}
        />
      </FormDrawer>
    </>
  );
};

export default ShipmentsSettings;
